import React from "react";
import classnames from "classnames";
import {each as _each, find as _find, map as _map} from 'lodash';
import {Button, ButtonSwitch, ButtonSwitchOption, Icon} from '@gisatcz/ptr-atoms';
import LayerControl from '../../LayerControl';

import backgroundLayers from "../../../data/layers/backgroundLayers";
import wmsLayers from "../../../data/layers/wmsLayers";

import "./style.scss";

const attributions = {
    osm: <>© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer noopener">OpenStreetMap</a> contributors</>,
    imagery: <>Esri, USGS | Esri, Garmin, FAO, NOAA | Earthstar Geographics</>,
    topomap: <>Esri, USGS | Esri, HERE, Garmin, FAO, NOAA, USGS</>,
    esriCanvas: <>Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ</>,
    cartoDark: (
        <>
            &copy;{' '}
            <a
                href="https://www.openstreetmap.org/copyright"
                target="_blank"
                rel="noreferrer noopener"
            >
                OpenStreetMap
            </a>{' '}
            contributors,{' '}
            <a
                href="https://creativecommons.org/licenses/by-sa/2.0/"
                target="_blank"
                rel="noreferrer noopener"
            >
                CC-BY-SA
            </a>
        </>
    ),
};

class LayersWrapper extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            open: false
        }

        this.changeState = this.changeState.bind(this);
        this.switchBackground = this.switchBackground.bind(this);
    }

    changeState() {
        this.setState({
            open: !this.state.open
        })
    }

    switchBackground(key) {
        this.props.setBackgroundLayer(backgroundLayers[key]);
    }

    onWmsLayerChange(key, e) {
        this.props.setWmsLayers(this.getWmsLayers(key));
    }

    getWmsLayers(layerKey) {
        const currentMapLayers = this.props.layers;

        let layers = [];
        _each(wmsLayers, wmsLayer => {
           const wmsLayerKey = wmsLayer.key;
           const presentInCurrentMapLayers = !!_find(currentMapLayers, mapLayer => mapLayer.key === wmsLayerKey);

           if (presentInCurrentMapLayers && wmsLayerKey !== layerKey) {
               layers.push(wmsLayer);
           } else if (!presentInCurrentMapLayers && wmsLayerKey === layerKey) {
               layers.push(wmsLayer);
           }
        });

        return layers;
    }

    render() {
        const classes = classnames("eo4sdDrrInsarMyanmar20-map-wrapper", {
           open: this.state.open
        });

        const backgroundLayerKey = this.props.backgroundLayer?.key;
        const currentMapKey = this.props.mapKey;

        // TODO Button is wrapped in ptr-map-controls just for styling.
        return (
            <div className="ptr-map-wrapper">
                <div className="ptr-map-controls eo4sdDrrInsarMyanmar20-layers-button">
                    <div className="control">
                        <Button icon="layers" onClick={this.changeState}/>
                    </div>
                </div>

                <div className="eo4sdDrrInsarMyanmar20-map-attribution">
                    <a href="https://leafletjs.com/" target="_blank" rel="noreferrer noopener">Leaflet</a> | Background map: {attributions[backgroundLayerKey]}
                </div>

                <div className={classes}>
                    <div className="eo4sdDrrInsarMyanmar20-map-wrapper-header">
                        <div><Icon icon="layers"/><span>Layers</span></div>
                        <Button icon="times" invisible primary onClick={this.changeState}/>
                    </div>
                    <div className="eo4sdDrrInsarMyanmar20-map-wrapper-content">
                        {currentMapKey === "risk-assessment-big" ? (this.renderRiskAssessmentLayers()) : null}
                        <div className="eo4sdDrrInsarMyanmar20-layer-controls-group">
                            <div className="eo4sdDrrInsarMyanmar20-layer-controls-group-header">WMS layers</div>
                            <div>{this.renderWmsLayers()}</div>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-layer-controls-group">
                            <div className="eo4sdDrrInsarMyanmar20-layer-controls-group-header">Background layers</div>
                            <div>{this.renderBackgroundLayers()}</div>
                        </div>
                    </div>
                </div>
                {this.props.children}
            </div>
        );
    }

    renderRiskAssessmentLayers() {
        return (
            <>
                <div className="eo4sdDrrInsarMyanmar20-layer-controls-group">
                    <div className="eo4sdDrrInsarMyanmar20-layer-controls-group-header">Topographic zones - exposure to:</div>
                    <LayerControl layerKey="risk-assessment-zones-big"/>
                </div>
                <div className="eo4sdDrrInsarMyanmar20-layer-controls-group">
                    <div className="eo4sdDrrInsarMyanmar20-layer-controls-group-header">Roads - exposure to:</div>
                    <LayerControl layerKey="risk-assessment-roads-big"/>
                </div>
            </>
        );
    }

    renderBackgroundLayers() {
        const activeLayerKey = this.props.backgroundLayer?.key;

        const options = _map(backgroundLayers, layer =>
            <ButtonSwitchOption
                key={layer.key}
                value={layer.key}
                active={layer.key === activeLayerKey}
            >
                {layer.name}
            </ButtonSwitchOption>
        );

        return (
            <ButtonSwitch onClick={this.switchBackground} ghost>
                {options}
            </ButtonSwitch>
        );
    }

    renderWmsLayers() {
        return wmsLayers.slice(0).reverse().map(wmsLayer => {
            const key = wmsLayer.key;
            const active = !!_find(this.props.layers, currentLayer => currentLayer.key === key);

            return (
                <div className="eo4sdDrrInsarMyanmar20-layer-control-wrapper">
                    <div className="eo4sdDrrInsarMyanmar20-layer-control">
                        <label>
                            <input type="checkbox" onChange={this.onWmsLayerChange.bind(this, key)} checked={active}/>
                            {wmsLayer.name}
                        </label>
                        </div>
                        {this.renderWmsLegend(key, active)}
                </div>
            );
        });
    }

    renderWmsLegend(key, active) {
        const legendClasses = classnames("eo4sdDrrInsarMyanmar20-layer-control-legend", {
            open: active
        });

        if (key === "susceptibility") {
            return (
                <div className={legendClasses}>
                    <div className="eo4sdDrrInsarMyanmar20-legend-susceptibility">
                        <div className="eo4sdDrrInsarMyanmar20-legend-bar"/>
                        <div className="eo4sdDrrInsarMyanmar20-legend-labels">
                            <span>0</span>
                            <span>1</span>
                        </div>
                    </div>
                </div>
            );
        } else if (key === "inventory") {
            return (
                <div className={legendClasses}>
                    <div className="eo4sdDrrInsarMyanmar20-legend-inventory">
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="14">
                                <polygon fill="#0101ff" points="7,1 1,14 14,14" className="triangle"/>
                            </svg>
                            <span>before 2015</span>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="14">
                                <polygon fill="#fbff01" points="7,1 1,14 14,14" className="triangle"/>
                            </svg>
                            <span>in 2015</span>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="14">
                                <polygon fill="#e8894e" points="7,1 1,14 14,14" className="triangle"/>
                            </svg>
                            <span>in or after 2015</span>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="14">
                                <polygon fill="#099a1f" points="7,1 1,14 14,14" className="triangle"/>
                            </svg>
                            <span>before, in or after 2015</span>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="14">
                                <polygon fill="#ff010d" points="7,1 1,14 14,14" className="triangle"/>
                            </svg>
                            <span>after 2015</span>
                        </div>
                    </div>
                </div>
            );
        } else if (key === "deformation_clusters") {
            return (
                <div className={legendClasses}>
                    <div className="eo4sdDrrInsarMyanmar20-legend-deformation-clusters">
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="18">
                                <polygon fill="transparent" points="3,3 3,11 15,11 15,3" strokeWidth="3" stroke="#000000" strokeLinejoin="round"/>
                            </svg>
                            <span>Risky cluster - from interpretation</span>
                        </div>
                        <div className="eo4sdDrrInsarMyanmar20-legend-item">
                            <svg height="14" width="18">
                                <polygon fill="transparent" points="3,3 3,11 15,11 15,3" strokeWidth="3" stroke="#ff0000" strokeLinejoin="round"/>
                            </svg>
                            <span>Risky cluster - ADA</span>
                        </div>
                    </div>
                </div>
            );
        } else {
            return null;
        }
    }
}

export default LayersWrapper;